import { SingletonRouter } from "next/router";
import React from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import {
  getOptions,
  getRefsForFlightOffer,
} from "shared/components/Flights/FlightOffer";
import { AppState } from "shared/data/reducers";
import { useSelectedStreamParts } from "shared/hooks/flightOffer";
import { lastElement } from "shared/lib/functions";
import IFlightOffer from "shared/types/flightOffer";

import BrandTabs from "@components/Flights/BrandedFares/BrandTabs";
import TravelPolicies, {
  keyFromSelectedStream,
} from "@components/Flights/TravelPolicies";
import FormattedMessage from "@components/UI/FormattedMessage";

import useNthFlightLink from "../../hooks/results/useNthFlightLink";
import useNthFlightShareInfo from "../../hooks/results/useNthFlightShareInfo";
import PriceFormatter from "../Formatters/PriceFormatter";
import FlightAdditionalInfo from "./FlightAdditionalInfo";
import FlightStream from "./FlightStream";

export default function FlightOffer({
  flightOffer,
  index,
  url,
  isCbt,
  setIsSomeModalOpenned,
}: {
  flightOffer: IFlightOffer;
  index: number | string;
  url: SingletonRouter;
  isCbt: boolean;
  setIsSomeModalOpenned: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const intl = useIntl();

  const {
    selectedCombination,
    selectedStreamParts,
    changeSelectedStreamParts,
  } = useSelectedStreamParts(flightOffer);

  const {
    travelPoliciesResults,
    travelPolicies,
    currency,
    isCbtEnabled,
    showServiceInfo,
    searchQuery,
    user,
  } = useSelector((state: AppState) => ({
    travelPoliciesResults: state.search.travelPoliciesResults,
    travelPolicies: state.search.travelPolicies,
    currency: state.search.currency,
    isCbtEnabled:
      state.storage?.frontendSettings?.dealerCorporateSettings?.enableCbt ===
      "true", // if cbt is enabled regardles if user is logged in
    showServiceInfo: state.search.toggleServiceInfo,
    searchQuery: state.storage.searchQuery,
    user: state.user,
  }));

  const key = keyFromSelectedStream(
    selectedStreamParts,
    selectedCombination.PricingDetailKey
  );

  const travelPoliciesResult = travelPoliciesResults?.[key];

  const refs = getRefsForFlightOffer(flightOffer, selectedStreamParts);

  const [flightLink, openFlight] = useNthFlightLink({
    selectedCombination,
    refs,
    policyResolve: travelPoliciesResult,
    brandCheckedBaggageService:
      selectedCombination.PricingDetail.BaggageLimit.Included,
    brandsDetailsAvailable: flightOffer?.BrandsDetailsAvailable === "true",
    flightOffer: { ...flightOffer, currency, user, selectedFlightSegment: key },
    intl,
  });

  const flightPricing = url.query?.currency
    ? selectedCombination.AlternativeCurrencyPricingDetail.FlightPricing
    : selectedCombination.PricingDetail.FlightPricing;
  const pricePerPassenger = flightPricing.FlightPassengerPrices.FlightPassengerPrice.find(
    (passenger) => passenger.Code !== "INF"
  ).DisplayPricePerPassenger;
  const passengerCount =
    flightPricing.FlightPassengerPrices.FlightPassengerPrice.length;

  let bestOffer = "";
  if (flightOffer.cheapest !== undefined) {
    bestOffer = "CHEAPEST";
  } else if (flightOffer.shortest !== undefined) {
    bestOffer = "SHORTEST";
  }

  const flightStreams = flightOffer.FlightItinerary.FlightStream;
  const firstFlightStream = Array.isArray(flightStreams)
    ? flightStreams[0]
    : flightStreams;

  const flightOriginAirport =
    firstFlightStream[0].FlightSegments.FlightSegment[0].OriginAirport;
  const flightDestinationAirport = lastElement(
    lastElement(firstFlightStream).FlightSegments.FlightSegment
  ).DestinationAirport;

  const flightDepartureDate =
    flightOffer.FlightItinerary.FlightStream[0][0].DepartureDateTime;
  const flightReturnDate =
    flightOffer.FlightItinerary.FlightStream.length > 1
      ? lastElement(lastElement(flightOffer.FlightItinerary.FlightStream))
          .ArrivalDateTime
      : null;

  const flightShareInfo = useNthFlightShareInfo({
    flightPricing,
    flightUrl: flightLink,
    flightOriginAirport,
    flightDestinationAirport,
    departureDate: flightDepartureDate,
    returnDate: flightReturnDate,
    searchQuery,
  });

  return (
    <div className="container" id={`flight-${index}`}>
      <BrandTabs
        flightOffer={flightOffer}
        setIsSomeModalOpenned={setIsSomeModalOpenned}
        changeSelectedStreamParts={changeSelectedStreamParts}
        selectedStreamParts={selectedStreamParts}
        url={url}
        currency={url.query?.currency || currency}
        isCbt={isCbt}
      />

      <div className="flight">
        {bestOffer === "CHEAPEST" && (
          <div className="flight-segments-best-offer-mobile">
            <FormattedMessage id="FlightOffer.goodPrice" />
          </div>
        )}
        {bestOffer === "SHORTEST" && (
          <div className="flight-segments-best-offer-mobile">
            <FormattedMessage id="FlightOffer.shortestFlight" />
          </div>
        )}

        <div className="flight-segments">
          <div className="flight-segment-service-info">
            {showServiceInfo &&
              selectedCombination?.PricingDetail?.InternalServiceInfo?.$t}
          </div>
          {flightOffer.FlightItinerary.FlightStream.map((flightStream, i) => (
            <FlightStream
              key={selectedStreamParts[i + 1]}
              flightStream={flightStream}
              index={i}
              changeSelectedStreamParts={changeSelectedStreamParts}
              selectedOptionKey={selectedStreamParts[i + 1]}
              options={getOptions(
                flightOffer.CurrentCombinations,
                i,
                selectedStreamParts
              )}
              offerKey={flightOffer.key}
            />
          ))}
        </div>

        <div>
          {bestOffer === "CHEAPEST" && (
            <div className="flight-segments-best-offer">
              <FormattedMessage id="FlightOffer.goodPrice" />
            </div>
          )}
          {bestOffer === "SHORTEST" && (
            <div className="flight-segments-best-offer">Nejkratší let</div>
          )}

          <div className="flight-prices-links">
            {passengerCount > 1 && (
              <div className="flight-prices-links-price-price-description">
                <FormattedMessage id="Flight.perOnePerson" />
              </div>
            )}
            <div className="flight-prices-links-price">
              <strong>
                <PriceFormatter
                  price={pricePerPassenger}
                  customCurrency={url.query?.currency || currency}
                />
              </strong>
            </div>
            <button
              type="button"
              className="button-secondary-color flight-prices-links-price-button"
              id={`flight-prices-links-price-button-${index}`}
              onClick={openFlight}
            >
              <FormattedMessage id="FlightOffer.pickflight" />
            </button>
            {passengerCount > 1 && (
              <div>
                <span className="flight-prices-links-price-sum-description">
                  <FormattedMessage id="Flight.allTravellers" />:{" "}
                  <PriceFormatter
                    price={flightPricing.FlightPrice.FullPrice}
                    customCurrency={url.query?.currency || currency}
                  />
                </span>
              </div>
            )}
            <TravelPolicies
              isTwoOneWay={flightOffer.CombinedOffer === "true"}
              travelPolicies={travelPolicies}
              travelPoliciesResult={travelPoliciesResult}
              isLowCostFlag={flightOffer.LowCost === "true"}
              isPrivateFareFlag={
                isCbtEnabled && flightOffer.PrivateFare === "true"
              }
            />
          </div>
        </div>
      </div>

      <FlightAdditionalInfo
        index={index}
        passengerCount={passengerCount}
        getRefsForFlightOffer={() =>
          getRefsForFlightOffer(flightOffer, selectedStreamParts)
        }
        setIsSomeModalOpenned={setIsSomeModalOpenned}
        page="results"
        selectedCombination={selectedCombination}
        shareInfo={flightShareInfo}
      />
    </div>
  );
}
